// A school's News stories, where the school is the whole subject (#3972,
// founder pick B of the 2026-10-12 placement mocks): the "In the news"
// section on its public college page, and one line on its Launchpad card.
//
// The index is derived at build time (scripts/build-news-search.mjs writes
// src/data/collegeNews.json) so neither page loads the story bodies to know
// that Rice has one; __tests__/collegeNews.test.js fails when the committed
// file drifts. Which schools a story is about is read ONLY through
// storySchoolIds (#3971's one tag field), never a second list.
import { storySchoolIds, kbId as normId } from './astraMovesData.js';
import { classApplies } from './newsFind.js';
import { noLongerApplies, localToday } from './newsCurrency.js';

const eventOf = (s) => s.eventDate || s.publishedAt || '';
const CLASS_KEYS = ['fromClass', 'toClass', 'onlyClass', 'state'];

// Only what the two surfaces render or decide on: the class rule, never the
// story text beyond its title and summary.
function entryOf(s) {
    const src = (s.sources || [])[0] || {};
    const moves = Object.fromEntries(CLASS_KEYS.filter((k) => s.moves?.[k] != null).map((k) => [k, s.moves[k]]));
    return {
        slug: s.slug,
        title: s.title,
        dek: s.listDek || s.dek || '',
        date: eventOf(s),
        dateLabel: s.eventLabel || 'Published',
        sourceLine: s.sourceLine || src.label || null,
        sourceUrl: src.url || null,
        ...(Object.keys(moves).length ? { moves } : {}),
        // The story's own window (#3971), so a story that no longer applies
        // leaves both surfaces the day it ends, without a rebuild.
        ...(s.endsOn ? { endsOn: s.endsOn } : {}),
        ...(s.superseded ? { superseded: s.superseded } : {}),
    };
}

// {kbId: [entry, ...]}, newest event first; the same story under every
// school it is tagged to.
export function deriveCollegeNews(stories) {
    const out = {};
    const sorted = [...stories].sort((a, b) => eventOf(b).localeCompare(eventOf(a)) || String(a.slug).localeCompare(String(b.slug)));
    for (const s of sorted) {
        if (!s?.slug) continue;
        for (const id of storySchoolIds(s)) (out[id] ||= []).push(entryOf(s));
    }
    return Object.fromEntries(Object.keys(out).sort().map((k) => [k, out[k]]));
}

// A school's stories that still apply on `today` (#3971's noLongerApplies:
// only a window that says so ends a story). A list row's university_id may
// carry hyphens; the index is keyed the KB's way.
export const collegeStories = (index, kbId, today = localToday()) => ((kbId && index?.[normId(kbId)]) || [])
    .filter((e) => !noLongerApplies({ ...e, eventDate: e.date }, today));

// The Launchpad card's line (founder pick B: "only when the story applies to
// the student's class"). A story with no class rule applies to everyone who
// lists the school. A class-limited one shows only when the class is known
// and inside it: a not-your-year story stays on /news, labelled there, and
// an unknown class never reads as "yours". A state's-students story stays off
// the card (the card cannot say whose state it is). Returns the newest such
// story and how many more there are, or null.
export function cardNews(index, kbId, cls, today = localToday()) {
    const fits = collegeStories(index, kbId, today).filter((e) => {
        if (e.moves?.state) return false;
        const named = ['fromClass', 'toClass', 'onlyClass'].some((k) => e.moves?.[k] != null);
        return !named || classApplies(e.moves, cls) === true;
    });
    return fits.length ? { story: fits[0], more: fits.length - 1 } : null;
}
